import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ListTodo, Download, Plus, Edit2 } from 'lucide-react';
import { api } from '../api/client';
import { useTenant } from '../context/TenantContext';
import { useAuth } from '../context/AuthContext';
import { useActiveTenant } from '../hooks/useActiveTenant';
import { WorkItemEditor, AddWorkItemButton } from '../components/WorkItemEditor';
import { UpgradeBanner } from '../components/UpgradeBanner';
import { FilterBar2 } from '../components/filters';

const STATUS_COLORS = {
  'Blocked': '#8E44AD', 'Behind': '#E74C3C', 'Due Date at Risk': '#F1C40F',
  'In Progress': '#3498DB', 'In Review': '#16A085', 'Not Started': '#BDC3C7',
  'Completed': '#2ECC71', 'Not Required': '#95A5A6',
};

const TasksPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { canEdit, isLowerTier, tenant } = useTenant();
  const { user } = useAuth();
  
  // TENANT ISOLATION: Use standardized hook for correct tenant ID
  const { tenantId: activeTenantId } = useActiveTenant();
  
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // New Filter Bar 2.0 state
  const [advancedFilters, setAdvancedFilters] = useState(() => {
    const status = searchParams.get('status');
    const assignee = searchParams.get('assignee');
    return {
      status: status ? status.split(',') : [],
      assignee: assignee ? assignee.split(',') : [],
    };
  });
  const [quickFilters, setQuickFilters] = useState({
    blocked_only: false,
    behind_only: false,
    at_risk_only: false,
    due_this_week: false,
    my_work_only: false,
  });
  const [filterOptions, setFilterOptions] = useState({ teams: [], features: [] });
  
  // Editor state
  const [showEditor, setShowEditor] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [selectedFeatureForCreate, setSelectedFeatureForCreate] = useState(null);

  // Load filters when tenant changes
  useEffect(() => { 
    if (activeTenantId) loadFilters(); 
  }, [activeTenantId]);
  
  // Reload tasks when filters or tenant change
  useEffect(() => { 
    if (activeTenantId) {
      loadTasks();
      // Update URL with current filters
      const params = new URLSearchParams();
      if (advancedFilters.status?.length > 0) params.set('status', advancedFilters.status.join(','));
      if (advancedFilters.assignee?.length > 0) params.set('assignee', advancedFilters.assignee.join(','));
      setSearchParams(params, { replace: true });
    }
  }, [advancedFilters, quickFilters, activeTenantId]);

  const loadFilters = async () => {
    try {
      const res = await api.getFilters();
      setFilterOptions({ teams: res.data.teams || [], features: res.data.features || [] });
    } catch (error) { console.error('Failed to load filters:', error); }
  };

  const loadTasks = async () => {
    try {
      setLoading(true);
      const params = {};
      
      // Multi-select filters
      if (advancedFilters.status?.length > 0) params.status = advancedFilters.status.join(',');
      if (advancedFilters.assignee?.length > 0) params.assignee = advancedFilters.assignee.join(',');
      if (advancedFilters.feature_id?.length > 0) params.feature_id = advancedFilters.feature_id[0];
      if (advancedFilters.project_id?.length > 0) params.project_id = advancedFilters.project_id[0];
      if (advancedFilters.team?.length > 0) params.team = advancedFilters.team[0];
      
      // Quick filters override status
      if (quickFilters.blocked_only) params.status = 'Blocked';
      if (quickFilters.behind_only) params.status = 'Behind';
      if (quickFilters.at_risk_only) params.status = 'Behind,Due Date at Risk,Blocked';
      if (quickFilters.my_work_only && user?.email) params.assignee = user.email;
      
      const res = await api.getTasks(params);
      setTasks(res.data.tasks || []);
    } catch (error) { console.error('Failed to load tasks:', error); }
    finally { setLoading(false); }
  };

  const formatCurrency = (v) => {
    if (v === null || v === undefined || isNaN(v)) return '$0';
    return Math.abs(v) >= 1e6 ? `$${(v/1e6).toFixed(1)}M` : Math.abs(v) >= 1e3 ? `$${(v/1e3).toFixed(1)}K` : `$${v.toFixed(0)}`;
  };

  const kpis = {
    total: tasks.length,
    completed: tasks.filter(t => t.status === 'Completed').length,
    inProgress: tasks.filter(t => t.status === 'In Progress').length,
    blocked: tasks.filter(t => t.status === 'Blocked').length,
    totalHours: tasks.reduce((s, t) => s + (t.hours_act || 0), 0)
  };

  // Editor handlers
  const handleAddTask = () => {
    setEditingTask(null);
    const featureId = filterOptions.features[0]?.id;
    setSelectedFeatureForCreate(featureId);
    setShowEditor(true);
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setSelectedFeatureForCreate(task.feature_id);
    setShowEditor(true);
  };

  const handleEditorSave = () => {
    loadTasks();
  };

  const handleEditorDelete = (id) => {
    setTasks(tasks.filter(t => t.id !== id));
  };

  return (
    <div>
      {isLowerTier() && <UpgradeBanner feature="in-app task editing" className="mb-6" />}
      
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[var(--clr-text-primary)] flex items-center gap-3">
            <ListTodo className="w-7 h-7 text-orange-600" />
            Tasks
          </h1>
          <p className="text-[var(--clr-text-secondary)] mt-1">Track all tasks across the hierarchy</p>
        </div>
        <div className="flex items-center gap-3">
          <AddWorkItemButton itemType="task" onClick={handleAddTask} />
          <a href={api.exportCSV('tasks', { 
            ...(advancedFilters.status?.length > 0 ? { status: advancedFilters.status.join(',') } : {}),
            ...(advancedFilters.assignee?.length > 0 ? { assignee: advancedFilters.assignee.join(',') } : {}),
          })} className="flex items-center gap-2 px-4 py-2 bg-[var(--clr-surface-subtle)] hover:bg-gray-200 rounded-lg text-sm font-medium" data-testid="export-csv-btn">
            <Download className="w-4 h-4" /> Export CSV
          </a>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-[var(--clr-surface-card)] rounded-lg border p-4"><p className="text-sm text-[var(--clr-text-secondary)] mb-1">Total Tasks</p><p className="text-2xl font-bold">{kpis.total}</p></div>
        <div className="bg-[var(--clr-surface-card)] rounded-lg border p-4"><p className="text-sm text-[var(--clr-text-secondary)] mb-1">Completed</p><p className="text-2xl font-bold text-[var(--clr-sev-healthy)]">{kpis.completed}</p></div>
        <div className="bg-[var(--clr-surface-card)] rounded-lg border p-4"><p className="text-sm text-[var(--clr-text-secondary)] mb-1">In Progress</p><p className="text-2xl font-bold text-[var(--clr-brand-primary)]">{kpis.inProgress}</p></div>
        <div className="bg-[var(--clr-surface-card)] rounded-lg border p-4"><p className="text-sm text-[var(--clr-text-secondary)] mb-1">Blocked</p><p className="text-2xl font-bold text-purple-600">{kpis.blocked}</p></div>
        <div className="bg-[var(--clr-surface-card)] rounded-lg border p-4"><p className="text-sm text-[var(--clr-text-secondary)] mb-1">Total Hours</p><p className="text-2xl font-bold">{kpis.totalHours.toLocaleString()}</p></div>
      </div>

      {/* Filter Bar 2.0 */}
      <FilterBar2
        pageType="tasks"
        filters={advancedFilters}
        onFiltersChange={setAdvancedFilters}
        quickFilters={quickFilters}
        onQuickFiltersChange={setQuickFilters}
        counts={{
          blocked: kpis.blocked,
          behind: tasks.filter(t => t.status === 'Behind').length,
          atRisk: tasks.filter(t => ['Behind', 'Due Date at Risk', 'Blocked'].includes(t.status)).length,
          myWork: tasks.filter(t => t.assignee === user?.email || t.owner === user?.email).length,
        }}
        currentUser={user?.email}
        showInsightsBar={true}
        className="mb-6"
      />

      <div className="bg-[var(--clr-surface-card)] rounded-lg border overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--clr-brand-primary)]"></div></div>
        ) : tasks.length === 0 ? (
          <div className="text-center py-12"><ListTodo className="w-12 h-12 text-gray-300 mx-auto mb-4" /><p className="text-[var(--clr-text-secondary)]">No tasks found</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[var(--clr-surface-subtle)] border-b">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Task</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Story / Feature</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Project</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Sprint</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Owner</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Team</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Hours</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Dates</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {tasks.filter(Boolean).map((t) => {
                  const isMatchedItem = t._is_matched === true;
                  const isParentContext = t._is_parent === true && !t._is_matched;
                  
                  return (
                  <tr 
                    key={t.id} 
                    className={`hover:bg-[var(--clr-surface-hover)] ${isMatchedItem ? 'bg-yellow-50 border-l-4 border-l-yellow-400' : ''}`}
                    data-testid={`task-row-${t.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {isMatchedItem && <span className="px-1.5 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 rounded">Match</span>}
                        <span className="font-medium text-[var(--clr-text-primary)] max-w-xs truncate">{t.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-[var(--clr-text-secondary)]"><div>{t.story || '-'}</div><div className="text-xs text-[var(--clr-text-muted)]">{t.feature}</div></td>
                    <td className="px-4 py-3 text-sm text-[var(--clr-text-secondary)]">{t.project_name}</td>
                    <td className="px-4 py-3"><span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium text-white" style={{ backgroundColor: STATUS_COLORS[t.status] || '#95A5A6' }}>{t.status || 'Unknown'}</span></td>
                    <td className="px-4 py-3 text-sm text-[var(--clr-text-secondary)]">{t.sprint || '-'}</td>
                    <td className="px-4 py-3 text-sm text-[var(--clr-text-secondary)]">{t.owner || '-'}</td>
                    <td className="px-4 py-3 text-sm text-[var(--clr-text-secondary)]">{t.team || '-'}</td>
                    <td className="px-4 py-3 text-sm text-[var(--clr-text-secondary)]">{t.hours_act || 0}/{t.hours_est || 0}</td>
                    <td className="px-4 py-3 text-xs text-[var(--clr-text-muted)]">{t.start_date && <div>S: {t.start_date}</div>}{t.due_date && <div>D: {t.due_date}</div>}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Work Item Editor Modal */}
      <WorkItemEditor
        isOpen={showEditor}
        onClose={() => { setShowEditor(false); setEditingTask(null); }}
        itemType="task"
        item={editingTask}
        parentId={selectedFeatureForCreate}
        projectId={advancedFilters.project_id?.[0] || tasks[0]?.project_id || filterOptions.features[0]?.project_id}
        tenantId={activeTenantId}
        onSave={handleEditorSave}
        onDelete={handleEditorDelete}
      />
    </div>
  );
};

export default TasksPage;
